/**
 * Authentication Context for Supabase
 *
 * Provides authentication state and methods throughout the application.
 * Uses Supabase Auth with login_id-based authentication.
 *
 * @module AuthContextSupabase
 */

/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import {
  signIn,
  signOutUser,
  getAuthErrorMessage,
  type SignInCredentials,
  type AuthenticationError,
} from '@/services/authServiceSupabase'

/**
 * Staff record from the database (linked to auth user)
 */
interface StaffRecord {
  id: string
  loginId: string
  role: string
  staffName: string
  authUserId?: string | null
}

/**
 * Authentication context type definition
 */
interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  /** The staff record linked to the current auth user */
  staffRecord: StaffRecord | null
  /** Whether the current user is an admin (login_id === 'admin' or role === 'admin') */
  isAdmin: boolean
  signInWithLoginId: (credentials: SignInCredentials) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Custom hook to access authentication context
 *
 * @throws Error if used outside AuthProvider
 * @returns Authentication context
 *
 * @example
 * ```typescript
 * const { user, isAdmin, staffRecord, signInWithLoginId, logout } = useAuth()
 * ```
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Transform database row to StaffRecord
 */
function transformToStaffRecord(
  row: Record<string, unknown>
): StaffRecord | null {
  if (!row) return null
  return {
    id: row.id as string,
    loginId: (row.login_id as string) || '',
    role: (row.role as string) || '',
    staffName: (row.staff_name as string) || '',
  }
}

/**
 * Authentication provider component for Supabase
 *
 * Manages authentication state and provides auth methods to the application.
 * Uses Supabase Auth with onAuthStateChange for persistent sessions.
 * Also fetches and provides the staff record for the authenticated user.
 *
 * @param children - Child components
 *
 * @example
 * ```typescript
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 * ```
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [staffRecord, setStaffRecord] = useState<StaffRecord | null>(null)

  /**
   * Fetch staff record for the given user email
   * Simple version without abort controller to avoid race condition issues
   */
  const fetchStaffRecord = useCallback(async (email: string | undefined) => {
    if (!email) {
      setStaffRecord(null)
      return
    }

    try {
      const { data, error } = await supabase
        .from('staffs')
        .select('id, login_id, role, staff_name')
        .eq('mail', email)
        .maybeSingle()
      if (error) {
        console.warn('Failed to fetch staff record:', error.message)
        setStaffRecord(null)
        return
      }

      setStaffRecord(data ? transformToStaffRecord(data) : null)
    } catch (err) {
      console.error('Error fetching staff record:', err)
      setStaffRecord(null)
    }
  }, [])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      // Fetch staff record after getting session
      if (session?.user?.email) {
        fetchStaffRecord(session.user.email)
      }
      setLoading(false)
    })

    // Subscribe to authentication state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
      // Fetch staff record on auth state change
      if (session?.user?.email) {
        fetchStaffRecord(session.user.email)
      } else {
        setStaffRecord(null)
      }
      setLoading(false)
    })

    // Cleanup subscription on unmount
    return () => subscription.unsubscribe()
  }, [fetchStaffRecord])

  /**
   * Sign in with login_id and password
   *
   * @param credentials - Login ID and password
   * @throws AuthenticationError with user-friendly message
   */
  const signInWithLoginId = useCallback(
    async (credentials: SignInCredentials): Promise<void> => {
      const result = await signIn(credentials)
      // Session and user state will be updated via onAuthStateChange
      setSession(result.session)
      setUser(result.user)
    },
    []
  )

  /**
   * Sign out the current user
   *
   * @throws AuthenticationError with user-friendly message
   */
  const logout = useCallback(async (): Promise<void> => {
    await signOutUser()
    setStaffRecord(null)
    // User state will be updated via onAuthStateChange
  }, [])

  /**
   * Compute isAdmin from staffRecord
   * Admin if login_id === 'admin' or role === 'admin'
   */
  const isAdmin = useMemo(() => {
    if (!staffRecord) return false
    return staffRecord.loginId === 'admin' || staffRecord.role === 'admin'
  }, [staffRecord])

  const value: AuthContextType = {
    user,
    session,
    loading,
    staffRecord,
    isAdmin,
    signInWithLoginId,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Re-export useful types
export type { AuthenticationError, SignInCredentials, StaffRecord }
export { getAuthErrorMessage }
